import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  BillingHostService,
  BillingService,
  Contract,
  ListBillableItemsRequestFilters,
  ProductPrices,
  WholesalePricingService,
} from '@galaxy/billing';
import { DateUtilities } from '@vendasta/uikit';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  share,
  shareReplay,
  skipWhile,
  switchMap,
  tap,
} from 'rxjs/operators';
import { VerifyContractApiResponse } from './contract-details/contract-details-api-response';
import { ContractDetailsSummary } from './contract-details/contract-details-summary';
import { PartnerService } from './partner.service';

const ONBOARDING_FEE_SKU = 'onboarding_fee';

@Injectable({ providedIn: 'root' })
export class ContractDetailsService {
  public summary$: Observable<ContractDetailsSummary>;

  private reloadSummary$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  private isLoading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private isFailed$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isMissing$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private currency$$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  constructor(
    private http: HttpClient,
    private billingHostService: BillingHostService,
    private billingService: BillingService,
    private pricingService: WholesalePricingService,
    @Inject('PARTNER_ID') partnerId$: Observable<string>,
    private readonly partnerService: PartnerService,
  ) {
    this.summary$ = combineLatest([partnerId$, this.reloadSummary$$]).pipe(
      skipWhile(([partnerId, _]) => partnerId === null),
      switchMap(([partnerId, _]) => this.updateSummaryFromApi(partnerId)),
      tap((summary) => this.currency$$.next(summary.subscriptionCurrency)),
      tap(() => this.isLoading$$.next(false)),
      shareReplay(1), // Cut down on triggering upstream api calls due to multiple subscribers
    );
  }

  get currency(): string {
    return this.currency$$.getValue();
  }

  get failed(): Observable<boolean> {
    return this.isFailed$$.asObservable();
  }

  get missing(): Observable<boolean> {
    return this.isMissing$$.asObservable();
  }

  reloadSummary(): void {
    this.reloadSummary$$.next(this.reloadSummary$$.getValue() + 1);
  }

  get loading(): Observable<boolean> {
    return this.isLoading$$.asObservable();
  }

  get contractVerified(): Observable<boolean> {
    return this.summary$.pipe(
      map(
        (contractSummary: ContractDetailsSummary) =>
          !(contractSummary.contractVerified === undefined || contractSummary.contractVerified === null),
      ),
    );
  }

  updateSummaryFromApi(partnerId: string): Observable<ContractDetailsSummary> {
    this.isFailed$$.next(false);
    this.isLoading$$.next(true);
    this.isMissing$$.next(false);

    const contract$ = this.billingService.getMultiContract([partnerId]).pipe(
      map((contractsMap) => {
        return contractsMap ? contractsMap[partnerId] : null;
      }),
      catchError((err) => {
        this.isFailed$$.next(true);
        err.code === 404 ? this.isMissing$$.next(true) : this.isFailed$$.next(true);
        return of(null);
      }),
      filter((contract) => !!contract),
      shareReplay(1), // Filter out null values thrown from catchError
    );

    const contractSubscription$ = contract$.pipe(
      filter((contract) => !!contract?.subscriptionId),
      switchMap((contract: Contract) => {
        const filters = new ListBillableItemsRequestFilters({
          skus: [contract?.subscriptionId],
          merchantId: partnerId,
        });
        return this.billingService.listBillableItems('', filters, null, 1);
      }),
      map((r) => (r?.results ? r?.results[0] : null)),
    );

    const subscriptionPricings$ = contract$.pipe(
      filter((contract) => !!contract?.subscriptionId),
      switchMap((contract) =>
        this.pricingService.getMultiWholesalePrices(partnerId, [contract.subscriptionId, ONBOARDING_FEE_SKU]),
      ),
    );

    const contractPricing$ = combineLatest([contractSubscription$, subscriptionPricings$]).pipe(
      map(([sub, pricing]) => {
        pricing.forEach((price) => {
          const priceIndex = price?.prices?.findIndex((p) => p.frequency === sub?.frequency);
          if (priceIndex !== -1) {
            price.prices = [price.prices[priceIndex]];
          }
        });
        return pricing;
      }),
      distinctUntilChanged((p1, p2) => {
        // If empty, this is emitting an empty object then an empty array?
        return (
          typeof p1 === typeof p2 &&
          typeof p1 !== 'undefined' &&
          Object.keys(p1 || {}).length === 0 &&
          Object.keys(p2 || {}).length === 0
        );
      }),
      shareReplay(1),
    );

    return combineLatest([contract$, contractPricing$]).pipe(
      map(([contract, pricing]) => this.makeContractDetailsSummary(contract, pricing)),
    );
  }
  // Combine contract details from multiple sources
  private makeContractDetailsSummary(contract: Contract, pricing: ProductPrices[]): ContractDetailsSummary {
    // Contract
    const signedDate = contract && contract.signed ? DateUtilities.stripTimeAndTimeZoneInfo(contract.signed) : null;
    const endDate = contract && contract.end ? DateUtilities.stripTimeAndTimeZoneInfo(contract.end) : null;
    const activatedDate =
      contract && contract.activated ? DateUtilities.stripTimeAndTimeZoneInfo(contract.activated) : null;
    const modifiedDate =
      contract && contract.modified ? DateUtilities.stripTimeAndTimeZoneInfo(contract.modified) : null;
    const verifiedDate =
      contract && contract.verified ? DateUtilities.stripTimeAndTimeZoneInfo(contract.verified) : null;
    const dimension = contract ? contract.dimension : null;
    const status = contract ? contract.status : null;
    const currency = contract ? contract.currency.toString() : '';
    const netD = contract ? contract.netD : 0;
    const creditLimit = contract && contract.creditLimit ? contract.creditLimit : 0;
    // Pricing
    const subscriptionIndex = pricing?.findIndex((p) => p.sku === contract.subscriptionId);
    const subscriptionPricing = pricing[subscriptionIndex]?.prices;
    const onBoardingFeeIndex = pricing?.findIndex((p) => p.sku === ONBOARDING_FEE_SKU);
    const onBoardingFeePricing = pricing[onBoardingFeeIndex]?.prices;
    const subscriptionPrice = subscriptionPricing ? subscriptionPricing[0].pricingRules[0].price : 0;
    const subscriptionFrequency = subscriptionPricing ? subscriptionPricing[0].frequency.toString().toLowerCase() : '';
    const billingStrategy = subscriptionPricing ? subscriptionPricing[0].strategy.toString().toLowerCase() : null;
    const onBoardingFeePrice = onBoardingFeePricing ? onBoardingFeePricing[0].pricingRules[0].price : 0;

    return new ContractDetailsSummary(
      contract ? contract.subscriptionId : null,
      status,
      dimension,
      subscriptionPrice,
      subscriptionFrequency,
      currency,
      netD,
      creditLimit,
      signedDate,
      endDate,
      activatedDate,
      modifiedDate,
      verifiedDate,
      onBoardingFeePrice,
      billingStrategy,
    );
  }

  private updateVerifyState(url: string, clientId: string, userEmail: string): Observable<VerifyContractApiResponse> {
    const data = {
      clientId: clientId,
      userId: userEmail,
    };

    const post: Observable<VerifyContractApiResponse> = this.http.post(url, data, { withCredentials: true }).pipe(
      map((resp) => new VerifyContractApiResponse(resp)),
      share(),
    );
    post.subscribe(() => this.reloadSummary());

    return post;
  }

  public verify(clientId: string, userEmail: string): Observable<VerifyContractApiResponse> {
    const url: string = this.billingHostService.appEngineHost + '/internalApi/v1/contract/verify/';
    return this.updateVerifyState(url, clientId, userEmail);
  }

  public unverify(clientId: string, userEmail: string): Observable<VerifyContractApiResponse> {
    const url: string = this.billingHostService.appEngineHost + '/internalApi/v1/contract/unverify/';
    return this.updateVerifyState(url, clientId, userEmail);
  }

  public resetContract(partnerId: string): Observable<null> {
    const url: string = this.billingHostService.appEngineHost + '/internalApi/v2/contract/reset/';
    const data = { clientId: partnerId };
    return this.http.post(url, data, { withCredentials: true }).pipe(map(() => null));
  }
}
